import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Download,
  FileText,
  FolderOpen,
  Inbox,
  Info,
  Loader,
  RotateCcw,
  Search,
  ShieldCheck,
  Upload,
  UploadCloud,
  X,
} from 'lucide-react';

import {
  commitImport,
  fetchDocumentTypes,
  MatchedDocument,
  PersonalizedDocumentType,
  UnmatchedDocument,
  validateZip,
} from '../../_services/personalizedDocuments.service';
import PersonalizedDocumentsLibrary from './PersonalizedDocumentsLibrary';
import '../data-ingestion/data-ingestion.css';
import './personalized-documents.css';

type Tab = 'upload' | 'library';

type Step = 1 | 2 | 3 | 4 | 5;
const STEP_LABELS = ['Instructions', 'Upload', 'Validate & Preview', 'Submit', 'Result'];

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const PersonalizedDocumentsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('upload');
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Setup
  const [documentTypes, setDocumentTypes] = useState<PersonalizedDocumentType[]>([]);
  const [documentTypeId, setDocumentTypeId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  // Optional availability window — applies to every document in this batch.
  const [visibleFrom, setVisibleFrom] = useState('');
  const [visibleUntil, setVisibleUntil] = useState('');

  // Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Validation
  const [validating, setValidating] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [matched, setMatched] = useState<MatchedDocument[]>([]);
  const [unmatched, setUnmatched] = useState<UnmatchedDocument[]>([]);
  const [totals, setTotals] = useState<{
    pdfsInZip: number;
    matched: number;
    unmatched: number;
    willCreate: number;
    willReplace: number;
  } | null>(null);
  const [matchedSearch, setMatchedSearch] = useState('');
  const [unmatchedSearch, setUnmatchedSearch] = useState('');

  // Confirm + commit
  const [showConfirm, setShowConfirm] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<{
    created: number;
    replaced: number;
    failures: { enrollmentId: number; fileName: string; reason: string }[];
  } | null>(null);

  useEffect(() => {
    fetchDocumentTypes()
      .then((types) => {
        setDocumentTypes(types);
        if (types.length > 0) setDocumentTypeId(types[0].Id);
      })
      .catch(() => {
        setUploadError('Could not load document types — check that nodefork is reachable.');
      });
  }, []);

  // Keyboard: ESC backs out one step (where safe)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showConfirm) {
          setShowConfirm(false);
          return;
        }
        if (currentStep > 1 && currentStep < 4 && !validating && !committing) {
          setCurrentStep((s) => (s - 1) as Step);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentStep, showConfirm, validating, committing]);

  const filteredMatched = useMemo(() => {
    const q = matchedSearch.trim().toLowerCase();
    if (!q) return matched;
    return matched.filter(
      (m) =>
        m.studentCode.toLowerCase().includes(q) ||
        (m.studentName || '').toLowerCase().includes(q) ||
        (m.batchName || '').toLowerCase().includes(q) ||
        m.fileName.toLowerCase().includes(q),
    );
  }, [matched, matchedSearch]);

  const filteredUnmatched = useMemo(() => {
    const q = unmatchedSearch.trim().toLowerCase();
    if (!q) return unmatched;
    return unmatched.filter(
      (u) =>
        u.fileName.toLowerCase().includes(q) || u.reason.toLowerCase().includes(q),
    );
  }, [unmatched, unmatchedSearch]);

  const processFile = (f: File) => {
    setUploadError(null);
    if (!f.name.toLowerCase().endsWith('.zip')) {
      setUploadError('Please upload a .zip file containing per-student PDFs.');
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      setUploadError('ZIP exceeds 200 MB. Split it into smaller batches.');
      return;
    }
    setFile(f);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processFile(f);
  };

  const canValidate =
    !!file && !!documentTypeId && title.trim().length > 0 && !validating;

  const handleValidate = async () => {
    if (!file || !documentTypeId || !title.trim()) return;
    setValidating(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      const res = await validateZip(
        file,
        Number(documentTypeId),
        title.trim(),
        (pct) => setUploadProgress(pct),
      );
      setSessionId(res.sessionId);
      setMatched(res.matched);
      setUnmatched(res.unmatched);
      setTotals(res.totals);
      setCurrentStep(3);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Validation failed.';
      setUploadError(msg);
    } finally {
      setValidating(false);
      setUploadProgress(null);
    }
  };

  const handleConfirm = async () => {
    if (!sessionId || !documentTypeId || !title.trim()) return;
    setShowConfirm(false);
    setCommitting(true);
    setCurrentStep(4);
    try {
      const res = await commitImport(
        sessionId,
        Number(documentTypeId),
        title.trim(),
        matched,
        { visibleFrom, visibleUntil },
      );
      setCommitResult(res);
      setCurrentStep(5);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Submit failed.';
      setCommitResult({ created: 0, replaced: 0, failures: [{ enrollmentId: 0, fileName: '', reason: msg }] });
      setCurrentStep(5);
    } finally {
      setCommitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setFile(null);
    setSessionId(null);
    setMatched([]);
    setUnmatched([]);
    setTotals(null);
    setMatchedSearch('');
    setUnmatchedSearch('');
    setCommitResult(null);
    setTitle('');
    setVisibleFrom('');
    setVisibleUntil('');
    setUploadError(null);
  };

  const downloadUnmatchedCsv = () => {
    if (unmatched.length === 0) return;
    const header = 'fileName,sizeBytes,reason';
    const rows = unmatched.map(
      (u) =>
        `"${u.fileName.replace(/"/g, '""')}",${u.sizeBytes},"${u.reason.replace(/"/g, '""')}"`,
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `personalized-doc-unmatched-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // --- Renderers ---

  const renderInstructions = () => (
    <div className="di-instructions">
      <div className="di-info-banner">
        <Info size={20} />
        <span>
          Bulk-upload personalized PDFs — one per student — using a ZIP of files
          named with each student's enrollment <strong>Student Code</strong> (e.g.{' '}
          <code>STU0042.pdf</code>). Use this for term results, answer scripts, report
          cards, or any document where each student gets their own copy.
        </span>
      </div>

      <div className="di-columns-section">
        <h3>How matching works</h3>
        <ul style={{ paddingLeft: 18, lineHeight: 1.7, fontSize: 14, color: 'var(--text-body)' }}>
          <li>
            Each PDF inside the ZIP must be named with the recipient's{' '}
            <strong>StudentCode</strong> followed by <code>.pdf</code>.
          </li>
          <li>
            Codes are matched against <strong>active</strong> student enrollments. Codes
            with multiple matches are skipped — disambiguate manually.
          </li>
          <li>
            Subfolders inside the ZIP are allowed; only the file name (not the path) is
            used to derive the student code.
          </li>
          <li>
            Re-uploading a document with the same title for the same student{' '}
            <strong>replaces</strong> the existing copy. The previous URL is kept on the
            row in case you need to roll back.
          </li>
        </ul>
      </div>

      <div className="di-tips">
        <h3>Tips for staff</h3>
        <ul>
          <li>
            Keep one ZIP per "batch" of documents (e.g. one ZIP for Term-1 Results, a
            different ZIP for Final Exam Answer Scripts).
          </li>
          <li>
            Give the batch a clear <strong>Title</strong> — students see this in iLearn.
          </li>
          <li>
            ZIP size limit: 200 MB. Split larger batches into two uploads.
          </li>
          <li>
            Unmatched PDFs don't block the import — they're listed for download so you can
            fix the filenames and re-upload.
          </li>
        </ul>
      </div>

      <div className="di-actions">
        <button className="di-btn-primary" onClick={() => setCurrentStep(2)}>
          Next: Upload ZIP <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderUpload = () => {
    const selectedType = documentTypes.find((t) => t.Id === documentTypeId);
    return (
      <div className="di-upload">
        <div className="pd-setup-row">
          <div>
            <label className="pd-field-label" htmlFor="pd-type">
              Document type
            </label>
            <select
              id="pd-type"
              className="pd-field-select"
              value={documentTypeId}
              onChange={(e) =>
                setDocumentTypeId(e.target.value ? Number(e.target.value) : '')
              }
              disabled={validating}
            >
              {documentTypes.length === 0 && <option value="">Loading...</option>}
              {documentTypes.map((t) => (
                <option key={t.Id} value={t.Id}>
                  {t.Name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="pd-field-label" htmlFor="pd-title">
              Batch title (visible to students)
            </label>
            <input
              id="pd-title"
              className="pd-field-input"
              type="text"
              placeholder={
                selectedType ? `e.g. ${selectedType.Name} — Term 1, 2026` : 'e.g. Term 1 Result 2026'
              }
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              disabled={validating}
            />
          </div>
        </div>

        <div className="pd-setup-row">
          <div>
            <label className="pd-field-label" htmlFor="pd-visible-from">
              Visible from (optional)
            </label>
            <input
              id="pd-visible-from"
              className="pd-field-input"
              type="datetime-local"
              value={visibleFrom}
              onChange={(e) => setVisibleFrom(e.target.value)}
              disabled={validating}
            />
          </div>
          <div>
            <label className="pd-field-label" htmlFor="pd-visible-until">
              Visible until (optional)
            </label>
            <input
              id="pd-visible-until"
              className="pd-field-input"
              type="datetime-local"
              value={visibleUntil}
              onChange={(e) => setVisibleUntil(e.target.value)}
              disabled={validating}
            />
          </div>
        </div>

        <div
          className={`di-dropzone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !validating && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div className="di-dropzone-icon">
            {file ? <FileText size={48} /> : <Upload size={48} />}
          </div>
          <div className="di-dropzone-text">
            {file ? (
              <>
                <strong>{file.name}</strong>
                <p>
                  {formatBytes(file.size)} · Click or drop another ZIP to replace
                </p>
              </>
            ) : (
              <>
                <strong>Drag & drop your ZIP here</strong>
                <p>or click to browse. Up to 200 MB.</p>
              </>
            )}
          </div>
        </div>

        {validating && uploadProgress !== null && (
          <div className="pd-upload-progress">
            <div className="pd-upload-progress-label">
              <span>Uploading & validating...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="pd-upload-progress-track">
              <div
                className="pd-upload-progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {uploadError && (
          <div className="di-error-banner">
            <AlertTriangle size={18} />
            <span>{uploadError}</span>
            <button className="di-error-close" onClick={() => setUploadError(null)}>
              <X size={16} />
            </button>
          </div>
        )}

        <div className="di-actions">
          <button
            className="di-btn-secondary"
            onClick={() => setCurrentStep(1)}
            disabled={validating}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button
            className="di-btn-primary"
            onClick={handleValidate}
            disabled={!canValidate}
          >
            {validating ? (
              <>
                <Loader size={18} className="di-spin" /> Validating...
              </>
            ) : (
              <>
                <ShieldCheck size={18} /> Validate & Preview
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderPreview = () => (
    <div className="di-preview">
      <div className="di-summary-bar">
        <div className="di-summary-item">
          <FileText size={18} />
          <span>
            <strong>{totals?.pdfsInZip ?? 0}</strong> PDFs in ZIP
          </span>
        </div>
        <div className="di-summary-item di-summary-valid">
          <CheckCircle size={18} />
          <span>
            <strong>{totals?.matched ?? 0}</strong> matched
          </span>
        </div>
        <div className="di-summary-item di-summary-error">
          <AlertTriangle size={18} />
          <span>
            <strong>{totals?.unmatched ?? 0}</strong> unmatched
          </span>
        </div>
      </div>

      <div className="pd-diff-summary">
        <span className="pd-chip pd-chip-new">
          <CheckCircle size={14} /> {totals?.willCreate ?? 0} will create
        </span>
        {(totals?.willReplace ?? 0) > 0 && (
          <span className="pd-chip pd-chip-replace">
            <RotateCcw size={14} /> {totals?.willReplace ?? 0} will replace existing
          </span>
        )}
        {(totals?.unmatched ?? 0) > 0 && (
          <span className="pd-chip pd-chip-skip">
            <X size={14} /> {totals?.unmatched ?? 0} skipped
          </span>
        )}
      </div>

      <div className="pd-split">
        <div className="pd-pane pd-pane-matched">
          <div className="pd-pane-head">
            <div className="pd-pane-title">
              <CheckCircle size={16} /> Matched students
            </div>
            <span className="pd-pane-count">{filteredMatched.length}</span>
          </div>
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              style={{ position: 'absolute', top: 22, left: 28, color: 'var(--text-faint)' }}
            />
            <input
              className="pd-pane-search"
              type="text"
              placeholder="Search by name, code, batch, file..."
              value={matchedSearch}
              onChange={(e) => setMatchedSearch(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
          </div>
          <div className="pd-pane-list">
            {filteredMatched.length === 0 ? (
              <div className="pd-pane-empty">
                <Inbox size={32} />
                <span>
                  {matched.length === 0
                    ? 'No matches yet — try a ZIP that contains files named with valid student codes.'
                    : 'No matches for your search.'}
                </span>
              </div>
            ) : (
              filteredMatched.map((m) => (
                <div key={`${m.enrollmentId}-${m.fileName}`} className="pd-row">
                  <div className="pd-row-avatar">{getInitials(m.studentName)}</div>
                  <div className="pd-row-body">
                    <div className="pd-row-name" title={m.studentName || ''}>
                      {m.studentName || m.studentCode}
                    </div>
                    <div
                      className="pd-row-meta"
                      title={`${m.studentCode} · ${m.batchName || ''} · ${m.fileName}`}
                    >
                      {m.studentCode}
                      {m.batchName ? ` · ${m.batchName}` : ''} · {formatBytes(m.sizeBytes)}
                    </div>
                  </div>
                  <span
                    className={`pd-row-tag ${
                      m.willReplaceDocumentId ? 'pd-tag-replace' : 'pd-tag-new'
                    }`}
                  >
                    {m.willReplaceDocumentId ? 'Replace' : 'New'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="pd-pane pd-pane-unmatched">
          <div className="pd-pane-head">
            <div className="pd-pane-title">
              <AlertTriangle size={16} /> Unmatched files
            </div>
            <span className="pd-pane-count">{filteredUnmatched.length}</span>
          </div>
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              style={{ position: 'absolute', top: 22, left: 28, color: 'var(--text-faint)' }}
            />
            <input
              className="pd-pane-search"
              type="text"
              placeholder="Search by file or reason..."
              value={unmatchedSearch}
              onChange={(e) => setUnmatchedSearch(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
          </div>
          <div className="pd-pane-list" style={{ padding: 0 }}>
            {filteredUnmatched.length === 0 ? (
              <div className="pd-pane-empty">
                <CheckCircle size={32} color="var(--status-success)" />
                <span>
                  {unmatched.length === 0
                    ? 'No unmatched files — perfect ZIP!'
                    : 'No unmatched files in your search.'}
                </span>
              </div>
            ) : (
              filteredUnmatched.map((u, i) => (
                <div key={`${u.fileName}-${i}`} className="pd-unmatched-row">
                  <div style={{ minWidth: 0 }}>
                    <div className="pd-unmatched-name" title={u.fileName}>
                      {u.fileName}
                    </div>
                    <div className="pd-unmatched-reason">{u.reason}</div>
                  </div>
                  <div className="pd-unmatched-size">{formatBytes(u.sizeBytes)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="di-actions" style={{ marginTop: 16 }}>
        <button className="di-btn-secondary" onClick={() => setCurrentStep(2)}>
          <ArrowLeft size={18} /> Back to Upload
        </button>
        {unmatched.length > 0 && (
          <button className="di-btn-secondary" onClick={downloadUnmatchedCsv}>
            <Download size={18} /> Download unmatched ({unmatched.length})
          </button>
        )}
        <button
          className="di-btn-primary"
          onClick={() => setShowConfirm(true)}
          disabled={matched.length === 0}
        >
          Submit {matched.length} document{matched.length !== 1 ? 's' : ''}{' '}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderSubmitting = () => (
    <div className="di-submitting">
      <div className="di-progress-container">
        <div className="di-progress-icon">
          <Upload size={40} className="di-spin" />
        </div>
        <h3>Uploading {matched.length} document{matched.length !== 1 ? 's' : ''} to storage...</h3>
        <p className="di-progress-text" style={{ marginTop: 16 }}>
          Each file is being uploaded to secure storage and linked to its student record.
          This may take a minute for large batches.
        </p>
      </div>
    </div>
  );

  const renderResult = () => {
    const success = commitResult && commitResult.created + commitResult.replaced > 0;
    return (
      <div className="di-result">
        {success ? (
          <div className="di-result-success">
            <div className="di-result-icon di-result-icon-success">
              <CheckCircle size={48} />
            </div>
            <h2>
              {commitResult!.failures.length === 0
                ? 'Import successful'
                : 'Import partially successful'}
            </h2>
            <p>
              {commitResult!.created + commitResult!.replaced} of {matched.length}{' '}
              document{matched.length !== 1 ? 's' : ''} processed. Students will see
              them under <strong>My Documents</strong> in iLearn.
            </p>
          </div>
        ) : (
          <div className="di-result-error">
            <div className="di-result-icon di-result-icon-error">
              <AlertTriangle size={48} />
            </div>
            <h2>Import failed</h2>
            <p>
              {commitResult?.failures[0]?.reason ||
                'Nothing was imported. Try again, or contact support if the problem persists.'}
            </p>
          </div>
        )}

        {commitResult && (
          <div className="pd-result-grid">
            <div className="pd-result-tile pd-result-tile-created">
              <div className="pd-result-tile-value">{commitResult.created}</div>
              <div className="pd-result-tile-label">Created</div>
            </div>
            <div className="pd-result-tile pd-result-tile-replaced">
              <div className="pd-result-tile-value">{commitResult.replaced}</div>
              <div className="pd-result-tile-label">Replaced</div>
            </div>
            <div className="pd-result-tile pd-result-tile-failed">
              <div className="pd-result-tile-value">{commitResult.failures.length}</div>
              <div className="pd-result-tile-label">Failed</div>
            </div>
          </div>
        )}

        {commitResult && commitResult.failures.length > 0 && (
          <div className="pd-failures">
            <div className="pd-failures-head">
              <AlertTriangle size={14} style={{ verticalAlign: 'middle' }} /> Failures
            </div>
            <ul className="pd-failures-list">
              {commitResult.failures.map((f, i) => (
                <li key={i}>
                  {f.fileName ? <strong>{f.fileName}</strong> : null}
                  {f.fileName ? ' — ' : null}
                  {f.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="di-actions di-actions-center">
          <button className="di-btn-primary" onClick={handleReset}>
            <RotateCcw size={18} /> Import another batch
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="data-ingestion pd-page">
      <div className="di-header">
        <h1>Personalized Documents</h1>
        <p>Bulk-upload per-student PDFs — results, answer scripts, report cards, anything personal.</p>
      </div>

      <div className="pd-tabs" role="tablist">
        <button
          className={`pd-tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
          role="tab"
          aria-selected={activeTab === 'upload'}
        >
          <UploadCloud size={16} /> Upload
        </button>
        <button
          className={`pd-tab ${activeTab === 'library' ? 'active' : ''}`}
          onClick={() => setActiveTab('library')}
          role="tab"
          aria-selected={activeTab === 'library'}
        >
          <FolderOpen size={16} /> Library
        </button>
      </div>

      {activeTab === 'upload' && (
        <>
          <div className="di-stepper">
            {STEP_LABELS.map((label, idx) => {
              const stepNum = (idx + 1) as Step;
              const isActive = stepNum === currentStep;
              const isCompleted = stepNum < currentStep;
              return (
                <React.Fragment key={stepNum}>
                  <div
                    className={`di-step ${isActive ? 'active' : ''} ${
                      isCompleted ? 'completed' : ''
                    }`}
                  >
                    <div className="di-step-circle">
                      {isCompleted ? <CheckCircle size={16} /> : stepNum}
                    </div>
                    <span className="di-step-label">{label}</span>
                  </div>
                  {idx < STEP_LABELS.length - 1 && (
                    <div className={`di-step-connector ${isCompleted ? 'completed' : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="di-content-card">
            {currentStep === 1 && renderInstructions()}
            {currentStep === 2 && renderUpload()}
            {currentStep === 3 && renderPreview()}
            {currentStep === 4 && renderSubmitting()}
            {currentStep === 5 && renderResult()}
          </div>
        </>
      )}

      {activeTab === 'library' && (
        <div className="di-content-card">
          <PersonalizedDocumentsLibrary />
        </div>
      )}

      <Modal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        centered
        dialogClassName="pd-confirm-modal"
      >
        <Modal.Header>
          <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
            Confirm import
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ margin: 0 }}>
            You're about to import <strong>{matched.length}</strong> personalized document
            {matched.length !== 1 ? 's' : ''} as{' '}
            <strong>
              "{title.trim()}" (
              {documentTypes.find((t) => t.Id === documentTypeId)?.Name || 'Document'})
            </strong>
            .
          </p>
          <div className="pd-confirm-summary">
            <div className="pd-confirm-cell">
              <div className="pd-confirm-cell-value">{totals?.willCreate ?? 0}</div>
              <div className="pd-confirm-cell-label">New</div>
            </div>
            <div className="pd-confirm-cell">
              <div className="pd-confirm-cell-value">{totals?.willReplace ?? 0}</div>
              <div className="pd-confirm-cell-label">Replace</div>
            </div>
            <div className="pd-confirm-cell">
              <div className="pd-confirm-cell-value">{totals?.unmatched ?? 0}</div>
              <div className="pd-confirm-cell-label">Skipped</div>
            </div>
          </div>
          {(visibleFrom || visibleUntil) && (
            <p style={{ marginTop: 16, fontSize: 13, color: 'var(--text-strong)' }}>
              <Info size={14} style={{ verticalAlign: 'middle' }} /> Visible to students{' '}
              {visibleFrom ? <>from <strong>{visibleFrom.replace('T', ' ')}</strong></> : 'immediately'}
              {visibleUntil ? <> until <strong>{visibleUntil.replace('T', ' ')}</strong></> : ' with no end date'}.
            </p>
          )}
          {(totals?.willReplace ?? 0) > 0 && (
            <p style={{ marginTop: 16, fontSize: 13, color: 'var(--status-warning-text)' }}>
              <Info size={14} style={{ verticalAlign: 'middle' }} /> Replacing existing
              documents keeps the previous URL on the row in case you need to roll back.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={committing}>
            {committing ? 'Importing...' : 'Yes, import'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PersonalizedDocumentsPage;
